
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useKits } from "@/hooks/useKits";
import { TickerPreview } from "@/components/TickerPreview";
import { useQueryClient } from "@tanstack/react-query";
import { useMessages } from "@/hooks/useMessages";
import { useAlerts } from "@/hooks/useAlerts";
import { useKitImages } from "@/hooks/useKitImages";
import { useKitLogo } from "@/hooks/useKitLogo";
import { useKitMarket } from "@/hooks/useKitMarket";
import { useTickerContent } from "@/hooks/useTickerContent";
import { useQuery } from "@tanstack/react-query";
import { TSPEvent } from "@/types/tsp";
import { format } from "date-fns";

export default function Ticker() {
  const { id } = useParams();
  const { kits, getKitByNumber } = useKits();
  const queryClient = useQueryClient();
  
  // Get the kit by its number first
  const { data: kit } = useQuery({
    queryKey: ["kit-by-number", id],
    queryFn: async () => {
      if (!id) return null;
      return getKitByNumber(id);
    },
  });

  const { messages } = useMessages(kit?.id);
  const { data: tickerContent = [] } = useTickerContent(kit?.id);
  const { alerts } = useAlerts(kit?.id);
  const { kitImageConfigs } = useKitImages(kit?.id);
  const { kitLogo } = useKitLogo(kit?.id);
  const { kitMarket } = useKitMarket(kit?.id);
  const [showUpcomingInTicker, setShowUpcomingInTicker] = useState(() => {
    const stored = localStorage.getItem('show-upcoming-in-ticker');
    return stored ? JSON.parse(stored) : false;
  });

  // Add upcoming games fetch
  const { data: upcomingGames = [] } = useQuery<TSPEvent[]>({
    queryKey: ['tsp-events'],
    queryFn: async () => {
      const response = await fetch('https://gotsp.live/wp-json/wp/v2/events?status=publish&per_page=100');
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      return response.json();
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 4 * 60 * 1000, // Consider data stale after 4 minutes
  });

  useEffect(() => {
    if (!kit?.id) return;

    console.log('Setting up polling failsafe');
    
    const pollInterval = setInterval(() => {
      console.log('Polling for updates...');
      queryClient.invalidateQueries({ queryKey: ['kit-messages', kit.id] });
      queryClient.invalidateQueries({ queryKey: ['alerts', kit.id] });
      queryClient.invalidateQueries({ queryKey: ['kit-images', kit.id] });
      queryClient.invalidateQueries({ queryKey: ['kits'] });
      queryClient.invalidateQueries({ queryKey: ['tsp-events'] });
      queryClient.invalidateQueries({ queryKey: ['ticker-content', kit.id] });
    }, 5000);

    return () => {
      console.log('Cleaning up polling interval');
      clearInterval(pollInterval);
    };
  }, [kit?.id, queryClient]);

  useEffect(() => {
    localStorage.setItem('show-upcoming-in-ticker', JSON.stringify(showUpcomingInTicker));
  }, [showUpcomingInTicker]);

  if (!kit) {
    return <div>Ticker not found</div>;
  }

  const filteredGames = upcomingGames
    .filter(event => 
      event.meta.event_status === "Live" || event.meta.event_status === "Scheduled"
    )
    .sort((a, b) => {
      if (a.meta.event_status === "Live" && b.meta.event_status !== "Live") return -1;
      if (b.meta.event_status === "Live" && a.meta.event_status !== "Live") return 1;
      const dateA = new Date(`${a.meta.event_date} ${a.meta.event_time}`);
      const dateB = new Date(`${b.meta.event_date} ${b.meta.event_time}`);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 5);

  // Combine messages, including upcoming games if enabled
  const activeMessages = [
    ...tickerContent.map(content => content.content),
    ...messages.filter(msg => msg.is_active).map(msg => msg.text)
  ];

  if (showUpcomingInTicker) {
    const upcomingGameMessages = filteredGames.map(game => 
      `${game.meta.event_away_team_short_name} vs ${game.meta.event_home_team_short_name} - ${format(new Date(`${game.meta.event_date} ${game.meta.event_time}`), 'MMM d, h:mm a')}`
    );
    activeMessages.push(...upcomingGameMessages);
  }

  const currentAlertMessage = alerts.find(alert => alert.is_active)?.text;

  return (
    <div className="fixed inset-0 bg-transparent flex flex-col">
      <div className="flex-1 bg-transparent"></div>
      <div className="w-screen h-[calc((100vw*80)/1920)] bg-transparent">
        <div className="w-full h-full flex justify-center items-end bg-transparent">
          <div className="w-full bg-transparent">
            <TickerPreview
              messages={activeMessages}
              speed={kit.speed}
              backgroundColor={kit.background_color}
              textColor={kit.text_color}
              alertMessage={currentAlertMessage}
              alertImages={kitImageConfigs}
              logo={kitLogo}
              selectedMarket={kitMarket?.market_short_name || "SAT"}
              weatherTemp={kitMarket?.weather_temp}
              weatherCondition={kitMarket?.weather_condition}
              upcomingGames={filteredGames}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
